import React, { useEffect, useRef } from 'react';
import { SimulationResult } from '../../types';

interface Props {
  result: SimulationResult | null;
  width?: number;
  height?: number;
}

/**
 * Animated bar chart showing |0⟩ vs |1⟩ measurement counts
 * or a distribution histogram for continuous experiments.
 */
export default function ProbabilityBar({ result, width = 340, height = 160 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef(0);
  const progressRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    progressRef.current = 0;
    cancelAnimationFrame(animRef.current);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, width, height);

      if (!result) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '12px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('Run an experiment to see results', width / 2, height / 2);
        return;
      }

      const t = Math.min(progressRef.current / 40, 1); // eased animation frames
      progressRef.current++;

      if (result.experimentType === 'entanglement' && result.jointCounts) {
        drawEntanglementBars(ctx, result, t, width, height);
      } else if (
        result.experimentType === 'qubit-circuit' ||
        result.experimentType === 'beam-splitter'
      ) {
        drawBinaryBars(ctx, result, t, width, height);
      } else {
        drawHistogram(ctx, result, t, width, height);
      }

      if (t < 1) animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [result, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ borderRadius: 8, display: 'block' }}
    />
  );
}

function drawBinaryBars(
  ctx: CanvasRenderingContext2D,
  result: SimulationResult,
  t: number,
  width: number,
  height: number
) {
  const total = result.measurementCounts.zero + result.measurementCounts.one;
  if (total === 0) return;

  const p0 = result.measurementCounts.zero / total;
  const p1 = result.measurementCounts.one / total;

  const pad = 30;
  const barW = (width - pad * 3) / 2;
  const maxH = height - 55;

  // Bar 0
  const h0 = maxH * p0 * t;
  ctx.fillStyle = '#6366f1';
  ctx.beginPath();
  ctx.roundRect(pad, height - 35 - h0, barW, h0, [4, 4, 0, 0]);
  ctx.fill();

  // Bar 1
  const h1 = maxH * p1 * t;
  ctx.fillStyle = '#f59e0b';
  ctx.beginPath();
  ctx.roundRect(pad * 2 + barW, height - 35 - h1, barW, h1, [4, 4, 0, 0]);
  ctx.fill();

  // Labels
  ctx.font = 'bold 13px monospace';
  ctx.textAlign = 'center';
  ctx.fillStyle = '#1e293b';
  ctx.fillText('|0⟩', pad + barW / 2, height - 18);
  ctx.fillText('|1⟩', pad * 2 + barW + barW / 2, height - 18);

  // Percentages
  ctx.font = '11px system-ui';
  ctx.fillStyle = '#475569';
  ctx.fillText(
    `${Math.round(p0 * 100 * t)}%`,
    pad + barW / 2,
    height - 35 - h0 - 5
  );
  ctx.fillText(
    `${Math.round(p1 * 100 * t)}%`,
    pad * 2 + barW + barW / 2,
    height - 35 - h1 - 5
  );

  // Title
  ctx.font = 'bold 11px system-ui';
  ctx.fillStyle = '#64748b';
  ctx.textAlign = 'left';
  ctx.fillText('Measurement Outcomes', 8, 14);

  ctx.font = '10px system-ui';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(
    `Total shots: ${total}`,
    8,
    26
  );
}

/**
 * 4-bar chart for entanglement: |00⟩  |01⟩  |10⟩  |11⟩
 * The |01⟩ and |10⟩ bars are always 0 – the visual "proof" of entanglement.
 */
function drawEntanglementBars(
  ctx: CanvasRenderingContext2D,
  result: SimulationResult,
  t: number,
  width: number,
  height: number
) {
  const jc = result.jointCounts!;
  const total = jc.zeroZero + jc.zeroOne + jc.oneZero + jc.oneOne;
  if (total === 0) return;

  const bars = [
    { label: '|00⟩', count: jc.zeroZero, color: '#6366f1', match: true },
    { label: '|01⟩', count: jc.zeroOne,  color: '#ef4444', match: false },
    { label: '|10⟩', count: jc.oneZero,  color: '#ef4444', match: false },
    { label: '|11⟩', count: jc.oneOne,   color: '#f59e0b', match: true },
  ];

  const pad = 12;
  const gap = 6;
  const barW = (width - pad * 2 - gap * 3) / 4;
  const maxH = height - 58;
  const baseline = height - 32;

  bars.forEach((bar, i) => {
    const p = bar.count / total;
    const barH = maxH * p * t;
    const x = pad + i * (barW + gap);

    // Bar fill
    ctx.fillStyle = bar.color;
    ctx.beginPath();
    ctx.roundRect(x, baseline - barH, barW, barH || 2, [3, 3, 0, 0]);
    ctx.fill();

    // Zero line for mismatched bars
    if (!bar.match) {
      ctx.strokeStyle = bar.color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, baseline);
      ctx.lineTo(x + barW, baseline);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Label
    ctx.font = 'bold 11px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1e293b';
    ctx.fillText(bar.label, x + barW / 2, baseline + 14);

    // Percentage
    ctx.font = '10px system-ui';
    ctx.fillStyle = bar.match ? bar.color : '#94a3b8';
    const pctY = barH > 10 ? baseline - barH - 4 : baseline - 8;
    ctx.fillText(
      bar.match ? `${Math.round(p * 100 * t)}%` : '0%',
      x + barW / 2,
      pctY
    );
  });

  // Legend / title
  ctx.font = 'bold 11px system-ui';
  ctx.fillStyle = '#64748b';
  ctx.textAlign = 'left';
  ctx.fillText('Joint Measurement Outcomes', 8, 14);

  ctx.font = '10px system-ui';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(`${total} shots  ·  ✓ matched every time`, 8, 26);
}

function drawHistogram(
  ctx: CanvasRenderingContext2D,
  result: SimulationResult,
  t: number,
  width: number,
  height: number
) {
  const dist = result.probabilityDistribution;
  if (!dist.length) return;

  const pad = { top: 24, bottom: 30, left: 8, right: 8 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const barW = plotW / dist.length;

  dist.forEach((val, i) => {
    const barH = plotH * val * t;
    const hue = result.hasInterference ? 240 : 30; // blue for interference, amber for classical
    ctx.fillStyle = `hsl(${hue},80%,55%)`;
    ctx.fillRect(pad.left + i * barW, pad.top + plotH - barH, barW - 0.5, barH);
  });

  // Axis baseline
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top + plotH);
  ctx.lineTo(width - pad.right, pad.top + plotH);
  ctx.stroke();

  // Title
  ctx.font = 'bold 11px system-ui';
  ctx.fillStyle = '#64748b';
  ctx.textAlign = 'left';
  ctx.fillText(
    result.hasInterference ? 'Interference Pattern' : 'Probability Distribution',
    8,
    14
  );
}
